import React, { useCallback, useState } from 'react';
import {
  useGetIdentityLocationCounts,
  useGetLocationUsers,
} from '../../../domain/Users';
import useClampedPage from '../../../hooks/useClampedPage';
import { DebouncedSearchField } from '../../commonComponents/DebouncedSearchField';
import { DsPagination } from '../../commonComponents/DsPagination';
import { ListPage } from '../../commonComponents/ListPage';
import { LocationUsersTable } from './components/LocationUsersTable';

/**
 * Location Users (GOTU-19601) — every office identity, newest first, from
 * auth-service's user search, with each one's Location Count from
 * organization-service.
 *
 * Search and paging are server-side; this page holds the two values and hands
 * them to the query. Incomplete sign-ups are listed on purpose: the endpoint
 * applies no status filter, and finding a half-made account is part of what
 * the page is for.
 */
export const LocationUsers = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const {
    users, totalCount, pageSize, isPending, isFetching, isError,
  } = useGetLocationUsers({ page, search });

  const { countsById, isError: isLocationCountsError } = useGetIdentityLocationCounts(
    users.map((user) => user.id),
  );

  // A page can outlive its data — narrowing the search leaves an out-of-range
  // page requesting an empty slice. Pull it back into range.
  useClampedPage(page, totalCount, pageSize, setPage);

  // Page 4 of every identity is not page 4 of the matches.
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  return (
    <ListPage
      title="Location Users"
      isLoading={isFetching && users.length > 0}
      total={totalCount ?? undefined}
      filters={(
        <DebouncedSearchField
          label="Search"
          placeholder="Name, email or phone"
          value={search}
          onChange={handleSearchChange}
        />
      )}
    >
      <LocationUsersTable
        users={users}
        locationCounts={countsById}
        isLocationCountsError={isLocationCountsError}
        isPending={isPending}
        isError={isError}
        hasSearch={search.trim() !== ''}
        footer={(
          <DsPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount ?? 0}
            onPageChange={setPage}
          />
        )}
      />
    </ListPage>
  );
};

export default LocationUsers;
